import { Metadata } from "next";
import { SERVICE_COUNTRY_NAME } from "@/constants/serviceFacts";
import { getDressCategory } from "@/constants/dressAttributeDescriptions";
import {
  ACCEPTED_PAYMENT_METHODS,
  RETURN_POLICY_COUNTRY,
  RETURN_POLICY_URL_PATH,
  SCHEMA_ACCEPTED_PAYMENT_METHODS,
  SCHEMA_RETURN_POLICY_CATEGORY,
} from "@/constants/merchantPolicy";
import {
  CUSTOM_DRESS_MAX_DISPLAY_PRICE_USD,
  CUSTOM_DRESS_STARTING_PRICE_USD,
  normalizePublicDressPrice,
} from "@/constants/pricing";

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string[];
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: string;
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
  structuredData?: Record<string, any>;
  noindex?: boolean;
}

// Page-specific metadata generator for Next.js App Router
export function generateSEOMetadata({
  title,
  description,
  canonical,
  keywords = [],
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogType = "website",
  noindex = false,
}: SEOProps): Metadata {
  const baseUrl =
    process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";
  const fullTitle = title.includes("Build-a-Dress")
    ? title
    : `${title} | Build-a-Dress`;
  const fullCanonical = canonical || baseUrl;
  const defaultOgImage = `${baseUrl}/og-image.jpg`;

  // Use custom dimensions if provided, otherwise use defaults (1200x630)
  const imageWidth = ogImageWidth || 1200;
  const imageHeight = ogImageHeight || 630;

  return {
    // The root layout adds the brand to document titles. Social titles stand alone.
    title,
    description,
    keywords: [
      "AI dress design",
      "custom dress",
      "custom dressmaker",
      "custom dresses for women",
      "custom made wedding dresses",
      "custom evening gowns",
      "custom prom dresses",
      "custom bridesmaid dresses",
      "custom formal dresses",
      "custom cocktail dresses",
      "custom plus size dresses",
      "custom petite dresses",
      "custom maternity dresses",
      "custom casual dresses",
      "custom party dresses",
      "custom designer dresses",
      "custom tailored dresses",
      "custom made to measure dresses",
      "bespoke dress",
      "made to order dress",
      "custom dress design",
      "custom dress consultation",
      "custom dress fitting",
      "custom dress alterations",
      "custom dress measurements",
      "custom dress online",
      "custom dress shops",
      "custom dress boutiques",
      "custom dress services",
      "custom dressmakers near me",
      "custom dress designers near me",
      "custom dress tailors near me",
      "custom dress seamstresses near me",
      "custom dress alterations near me",
      "custom dress fittings near me",
      "custom dress consultations near me",
      "custom dress services near me",
      "fashion design",
      "personalized clothing",
      "dress maker",
      "wedding dress",
      "custom clothing",
      "custom couture dresses",
      "custom luxury dresses",
      "custom high fashion dresses",
      "custom artisan dresses",
      "custom handmade dresses",
      "custom vintage dresses",
      "custom ball gowns",
      "custom pageant dresses",
      "custom homecoming dresses",
      "custom mother of the bride dresses",
      "custom A-line dresses",
      "custom sheath dresses",
      "custom mermaid dresses",
      "custom fit and flare dresses",
      "custom wrap dresses",
      "custom off-the-shoulder dresses",
      "custom strapless dresses",
      "custom halter dresses",
      "custom silk dresses",
      "custom chiffon dresses",
      "custom satin dresses",
      "custom velvet dresses",
      "custom lace dresses",
      "custom sequin dresses",
      "custom beaded dresses",
      "custom embroidered dresses",
      "custom floral dresses",
      "custom printed dresses",
      "custom affordable dresses",
      "custom budget-friendly dresses",
      "custom luxury dresses",
      "custom high-end dresses",
      "custom designer dresses",
      "custom couture dresses",
      "custom bespoke dresses",
      "custom eco-friendly dresses",
      "custom sustainable dresses",
      "custom organic dresses",
      "custom vegan dresses",
      "custom recycled dresses",
      "custom upcycled dresses",
      "custom cultural dresses",
      "custom ethnic dresses",
      "custom traditional dresses",
      "custom fusion dresses",
      "custom modern dresses",
      "custom contemporary dresses",
      "custom minimalist dresses",
      "custom avant-garde dresses",
      "custom summer dresses",
      "custom spring dresses",
      "custom fall dresses",
      "custom winter dresses",
      "custom holiday dresses",
      "custom Christmas dresses",
      "custom New Year's Eve dresses",
      "custom Valentine's Day dresses",
      "custom Easter dresses",
      "custom Halloween dresses",
      "custom beach dresses",
      "custom resort dresses",
      "custom travel dresses",
      "custom destination wedding dresses",
      "custom elopement dresses",
      "custom work dresses",
      "custom office dresses",
      "custom business dresses",
      "custom professional dresses",
      "custom uniform dresses",
      "custom performance dresses",
      "custom stage dresses",
      "custom dance dresses",
      "custom recital dresses",
      "custom choir dresses",
      "custom theater dresses",
      "custom costume dresses",
      ...keywords,
    ],
    authors: [{ name: "Build-a-Dress Team" }],
    creator: "Build-a-Dress",
    publisher: "Build-a-Dress",
    alternates: {
      canonical: fullCanonical,
    },
    robots: {
      index: !noindex,
      follow: !noindex,
      googleBot: {
        index: !noindex,
        follow: !noindex,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    },
    openGraph: {
      type: ogType as any,
      locale: "en_US",
      url: fullCanonical,
      title: fullTitle,
      description,
      siteName: "Build-a-Dress",
      images: [
        {
          url: ogImage || defaultOgImage,
          width: imageWidth,
          height: imageHeight,
          alt: `${fullTitle} - Custom dress design and creation`,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title: fullTitle,
      description,
      images: [ogImage || defaultOgImage],
      creator: "@buildadress",
      site: "@buildadress",
    },
    verification: {
      google: process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
    },
    other: {
      // Additional meta tags for better Google search results
      image: ogImage || defaultOgImage,
      "image:width": imageWidth.toString(),
      "image:height": imageHeight.toString(),
      "image:alt": `${fullTitle} - Custom dress design and creation`,
    },
  };
}

// Shared structured data constants for Google Merchant Center compliance
const BASE_URL =
  process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";

// Shipping rates and delivery windows must come from an individual order or
// a confirmed merchant shipping policy. Do not publish guessed offer defaults.

/**
 * Voluntary merchant return policy exposed in Product JSON-LD. We do not
 * accept customer-remorse returns (hence `MerchantReturnNotPermitted`),
 * but defective items are refunded under statutory consumer protection
 * law — that carve-out is described at `RETURN_POLICY_URL_PATH`.
 * `merchantReturnDays` is intentionally omitted: it only applies to
 * finite-window policies.
 */
export const defaultReturnPolicy = {
  "@type": "MerchantReturnPolicy",
  url: `${BASE_URL}${RETURN_POLICY_URL_PATH}`,
  returnPolicyCountry: RETURN_POLICY_COUNTRY,
  returnPolicyCategory: SCHEMA_RETURN_POLICY_CATEGORY,
  applicableCountry: [RETURN_POLICY_COUNTRY],
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/FreeReturn",
} as const;

/**
 * Payment-method fields shared across every Offer/AggregateOffer in our
 * Product schemas. `acceptedPaymentMethod` uses the schema.org enumeration
 * (which only includes credit/debit cards), while `paymentAccepted` is a
 * free-text fallback Google reads to surface wallet support like Apple Pay
 * and Google Pay in product listings.
 */
export const defaultPaymentMethods = {
  acceptedPaymentMethod: SCHEMA_ACCEPTED_PAYMENT_METHODS.map(url => ({
    "@type": "PaymentMethod",
    "@id": url,
  })),
  paymentAccepted: ACCEPTED_PAYMENT_METHODS.join(", "),
} as const;

// Structured data generators
export function generateBreadcrumbSchema(
  breadcrumbs: Array<{ name: string; url: string }>
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/**
 * The one Product + Offer schema for a gallery dress page. Google reads it for
 * both product snippets and merchant listings, so it must describe exactly
 * one real offer. Don't add a ProductModel/ProductGroup with invented
 * color/size variants: every dress is a single made-to-measure item, and that
 * block failed Merchant validation on every page.
 *
 * Pass the Merchant feed's `sku` (the dress id) and `mpn` so the page and the
 * feed describe the same product.
 */
export function generateSimpleProductSchema({
  name,
  description,
  image,
  url,
  price = 1250,
  currency = "USD",
  availability = "InStock",
  dressType,
  category,
  sku,
  mpn,
}: {
  name: string;
  description: string;
  image: string;
  url: string;
  price?: number;
  currency?: string;
  availability?: string;
  dressType?: "wedding" | "prom" | "bridesmaid" | "quinceanera" | "formal";
  category?: string;
  sku?: string;
  mpn?: string;
}) {
  const baseSku = sku || name.toLowerCase().replace(/\s+/g, "-");

  // Get Google product category from dressType if not provided
  let googleCategory = category;
  if (!googleCategory && dressType) {
    googleCategory = getDressCategory(dressType);
  }

  // Simple Product schema for product snippets
  const schema: any = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    url,
    sku: baseSku,
    mpn: mpn || baseSku,
    image: image, // Single image URL string (not array) for product snippets
    brand: {
      "@type": "Brand",
      name: "Build-a-Dress",
    },
    manufacturer: {
      "@type": "Organization",
      name: "Build-a-Dress",
    },
    itemCondition: "https://schema.org/NewCondition",
    offers: {
      "@type": "Offer",
      price: normalizePublicDressPrice(price).toString(),
      priceCurrency: currency,
      availability: `https://schema.org/${availability}`,
      url: url,
      hasMerchantReturnPolicy: defaultReturnPolicy,
      ...defaultPaymentMethods,
    },
  };

  // Add Google product category if available
  if (googleCategory) {
    schema.category = googleCategory;
  }

  return schema;
}

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Build-a-Dress",
    url: process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com",
    logo: `${process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com"}/logo/flat.svg`,
    description:
      "Design your own custom dress from a photo, sketch, or idea. Review your design and upfront price, then have your dress made to measure and delivered.",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@build-a-dress.com",
    },
    sameAs: ["https://www.tiktok.com/@buildadress"],
  };
}

export function generateWebSiteSchema() {
  const baseUrl =
    process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Build-a-Dress",
    url: baseUrl,
    description:
      "Design your own custom dress online from a photo, sketch, or idea, with upfront pricing and a fit based on your measurements.",
    publisher: {
      "@type": "Organization",
      name: "Build-a-Dress",
    },
  };
}

export function generateServiceSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Custom Dress Design",
    description:
      "Design your own custom dress from a photo, sketch, or idea. Build-a-Dress helps you review the design and upfront price, then have it made to your measurements and delivered.",
    provider: {
      "@type": "Organization",
      name: "Build-a-Dress",
    },
    serviceType: "Custom Clothing Design",
    areaServed: { "@type": "Country", name: SERVICE_COUNTRY_NAME },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: CUSTOM_DRESS_STARTING_PRICE_USD.toString(),
      highPrice: CUSTOM_DRESS_MAX_DISPLAY_PRICE_USD.toString(),
      offerCount: "1",
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Dress Design Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Dress Design Generation",
            description:
              "Generate custom dress designs using AI from text descriptions",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Custom Dress Manufacturing",
            description:
              "Custom dressmaking based on your approved design and measurements",
          },
        },
      ],
    },
  };
}

// Client-side component for structured data injection
export function StructuredData({ data }: { data: Record<string, any> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

// SEO helper for dynamic pages
export function createDynamicSEO({
  title,
  description,
  path,
  structuredData,
  ...props
}: SEOProps & { path: string }) {
  const baseUrl =
    process.env.NEXT_PUBLIC_BASE_URL || "https://build-a-dress.com";
  const canonical = `${baseUrl}${path}`;

  return generateSEOMetadata({
    title,
    description,
    canonical,
    structuredData,
    ...props,
  });
}
